import { getCompatibleCodesForSystem } from 'elements/hardwareSelector/SearchToolbar'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { ComponentTypes } from 'types/selectComponent'
import { ActionType } from 'types/tracking'
import type {
  FilterComponentRendererType,
  FilterGenericPropsType,
  FilterKeysType,
  FilterValuesType,
  OptionGenericType,
} from '../type'

export type FilterOptionsType = OptionGenericType<string>[]

type FilterProps = FilterGenericPropsType & {
  RendererComponent: FilterComponentRendererType<FilterOptionsType>
  filterKey: FilterKeysType
  id: string
  componentType: ComponentTypes
}

const defaultValue = ''

const SYSTEM_COMPATIBILITY_OPTIONS: FilterOptionsType = [
  { id: 'default', title: 'All', value: '' },
  { id: 'yes', title: 'Yes', value: '1' },
]

const getBatteryCompatibilityValue = ({
  filterValues,
  filterKey,
}: {
  filterValues: FilterValuesType
  filterKey: FilterKeysType
}) => {
  const codes = filterValues[filterKey]
  const compatible = !!codes && getCompatibleCodesForSystem('battery').join(',') === codes
  return compatible ? '1' : undefined
}

// TODO: getCompatibleCodesForSystem not responsive
const SystemCompatibilityFilter = ({
  id,
  filterKey,
  modifiableFilterValues,
  persistentFilterValues,
  allFilterValues,
  setModifiableFilters,
  RendererComponent,
  componentType,
}: FilterProps) => {
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'battery-compatibility-filter',
    eventName: 'Battery Compatibility Filter',
    description: 'Tracking battery compatibility filter interactions',
  })

  const translate = useTranslate()

  const onSelect = useCallback(
    (value) => {
      if (componentType === 'battery') {
        trackUserInteraction(
          { type: ActionType.SELECT },
          {
            eventName: 'Battery Compatibility Filter Changed',
            filterValue: value,
            previousValue: getBatteryCompatibilityValue({ filterValues: allFilterValues, filterKey }),
            compatibleBatteryCount: getCompatibleCodesForSystem('battery').length,
          }
        )

        if (value === '1') {
          setModifiableFilters({
            ...modifiableFilterValues,
            [filterKey]: getCompatibleCodesForSystem('battery').join(','),
          })
        } else {
          if (!!modifiableFilterValues[filterKey]) {
            const newModifiableFilterValues = { ...modifiableFilterValues }
            delete newModifiableFilterValues[filterKey]
            setModifiableFilters(newModifiableFilterValues)
          }
        }
      } else {
        trackUserInteraction(
          { type: ActionType.SELECT },
          {
            eventName: 'System Compatibility Filter Changed',
            filterValue: value,
          }
        )
        setModifiableFilters({
          ...modifiableFilterValues,
          [filterKey]: value,
        })
      }
    },
    [modifiableFilterValues, allFilterValues, filterKey]
  )

  const disabled =
    componentType === 'battery'
      ? getCompatibleCodesForSystem('battery').length === 0
      : allFilterValues['other_component_type'] !== 'dc_optimizer'

  const value =
    componentType === 'battery'
      ? getBatteryCompatibilityValue({ filterValues: allFilterValues, filterKey })
      : allFilterValues[filterKey] || defaultValue

  return (
    <RendererComponent
      id={id}
      defaultValue={''}
      label={translate('Compatible with system')}
      options={SYSTEM_COMPATIBILITY_OPTIONS}
      disabled={disabled}
      onSelect={onSelect}
      value={value}
    />
  )
}

export default SystemCompatibilityFilter
